import {useEffect, useState, useCallback, useRef, useId} from 'react'
import {InputRange} from 'dom-input-range'
import type {ChronicleCommand} from '../../hooks/use-chronicle-autocomplete'
import styles from './ChronicleAutocomplete.module.css'

interface ChronicleAutocompleteProps {
  suggestions: ChronicleCommand[]
  onSelect: (command: string) => void
  onDismiss?: () => void
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  currentText: string
}

/**
 * Dropdown that shows matching chronicle commands as the user types.
 * Positioned right below the caret using dom-input-range.
 * Supports keyboard navigation (ArrowUp/Down, Enter, Escape).
 */
export function ChronicleAutocomplete({
  suggestions,
  onSelect,
  onDismiss,
  textAreaRef,
  currentText,
}: ChronicleAutocompleteProps) {
  const [caretPos, setCaretPos] = useState<{top: number; left: number}>({top: 0, left: 0})
  const [activeIndex, setActiveIndex] = useState(0)
  const [hasNavigated, setHasNavigated] = useState(false)
  const instanceId = useId()
  const listboxId = `chronicle-autocomplete-listbox-${instanceId}`

  // Clamp active index to valid range when suggestions shrink
  const clampedIndex = suggestions.length > 0 ? activeIndex % suggestions.length : 0
  const clampedIndexRef = useRef(clampedIndex)

  // Keep ref in sync and set aria-activedescendant for screen readers
  useEffect(() => {
    clampedIndexRef.current = clampedIndex
    const textarea = textAreaRef.current
    if (!textarea || suggestions.length === 0) return

    const activeOptionId = suggestions[clampedIndex] ? `chronicle-option-${instanceId}-${clampedIndex}` : ''
    textarea.setAttribute('aria-activedescendant', activeOptionId)

    return () => {
      textarea.removeAttribute('aria-activedescendant')
    }
  }, [suggestions, clampedIndex, textAreaRef, instanceId])

  // Reset navigation state when suggestions change
  const [prevSuggestionsLength, setPrevSuggestionsLength] = useState(suggestions.length)
  if (prevSuggestionsLength !== suggestions.length) {
    setPrevSuggestionsLength(suggestions.length)
    setHasNavigated(false)
    setActiveIndex(0)
  }

  useEffect(() => {
    if (suggestions.length === 0) return
    const textarea = textAreaRef.current
    if (!textarea) return

    try {
      const selStart = textarea.selectionStart ?? currentText.length
      const range = new InputRange(textarea, selStart)
      const rangeRect = range.getBoundingClientRect()

      setCaretPos({
        top: rangeRect.bottom,
        left: rangeRect.left,
      })
    } catch {
      // Fallback: position below textarea start
      const textareaRect = textarea.getBoundingClientRect()
      setCaretPos({top: textareaRect.top + 24, left: textareaRect.left + 8})
    }
  }, [suggestions, currentText, textAreaRef])

  const handleSelect = useCallback(
    (command: string) => {
      onSelect(command)
      textAreaRef.current?.focus()
    },
    [onSelect, textAreaRef],
  )

  // Keyboard navigation
  const onDismissRef = useRef(onDismiss)

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    if (suggestions.length === 0) return
    const textarea = textAreaRef.current
    if (!textarea) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHasNavigated(true)
          setActiveIndex(i => (i + 1) % suggestions.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setHasNavigated(true)
          setActiveIndex(i => (i - 1 + suggestions.length) % suggestions.length)
          break
        case 'Enter': {
          if (!hasNavigated) break
          e.preventDefault()
          e.stopPropagation()
          const cmd = suggestions[clampedIndexRef.current]
          if (cmd) handleSelect(cmd.key)
          break
        }
        case 'Tab': {
          // Only intercept Tab if user has explicitly navigated with arrows
          if (!hasNavigated) break
          e.preventDefault()
          e.stopPropagation()
          const cmd = suggestions[clampedIndexRef.current]
          if (cmd) handleSelect(cmd.key)
          break
        }
        case 'Escape':
          e.preventDefault()
          e.stopPropagation()
          onDismissRef.current?.()
          break
      }
    }

    textarea.addEventListener('keydown', handleKeyDown)
    return () => textarea.removeEventListener('keydown', handleKeyDown)
  }, [suggestions, handleSelect, textAreaRef, hasNavigated])

  if (suggestions.length === 0) return null

  const dropdownTop = caretPos.top + 4

  return (
    <ul
      id={listboxId}
      className={styles.dropdown}
      role="listbox"
      aria-label="Chronicle command suggestions"
      style={
        {
          '--dropdown-top': `${dropdownTop}px`,
          top: `${dropdownTop}px`,
          left: `${caretPos.left}px`,
        } as React.CSSProperties
      }
    >
      {suggestions.map((cmd, index) => (
        <li
          key={cmd.key}
          id={`chronicle-option-${instanceId}-${index}`}
          role="option"
          aria-selected={index === clampedIndex}
          className={`${styles.item} ${index === clampedIndex ? styles.itemActive : ''}`}
          onMouseDown={e => {
            e.preventDefault()
            handleSelect(cmd.key)
          }}
        >
          <span className={styles.commandRow}>
            <span className={styles.commandLabel}>{cmd.key}</span>
            <span className={styles.commandDescription}>{cmd.description}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}
